// Formats duration (in days) into a human-readable string using years, months, weeks and days
// Shows at most TWO time units (largest-first). For example, if there are months and weeks,
// days are omitted.
// Examples:
// 1 -> "1 день"
// 7 -> "1 тиждень"
// 9 -> "1 тиждень і 2 дні"
// 30 -> "1 місяць"
// 37 -> "1 місяць і 1 тиждень"
// 45 -> "1 місяць і 2 тижні"
// 65 -> "2 місяці і 1 тиждень"
// 68 -> "2 місяці і 1 тиждень" (дні опускаємо, бо максимум два періоди)
// If days < 1 or falsy => empty string

import { getLanguage } from 'translation';

// Site language codes that differ from BCP 47.
const INTL_LOCALE = { ua: 'uk', no: 'nb' };

// Translations carry three forms per unit (_one / _few / _many, the Ukrainian
// set). Pick the category with the site language's own plural rules and fold
// anything else (other, two, zero) into the general plural, _many.
export const pluralCategory = (n, lang = getLanguage()) => {
  let category = 'other';
  try {
    category = new Intl.PluralRules(INTL_LOCALE[lang] || lang).select(Math.abs(n));
  } catch (e) {
    /* unknown locale: fall through to the general plural */
  }
  return category === 'one' || category === 'few' ? category : 'many';
};

const unitKey = (unit, n) => `DurationUnit${unit}_${pluralCategory(n)}`; // e.g. DurationUnitDay_one

const formatDuration = (days, t) => {
  if (!days || isNaN(days) || days < 1) return '';
  const totalDays = Number(days);

  let remaining = totalDays;

  // Calculate years first (365 days per year)
  const years = Math.floor(remaining / 365);
  remaining -= years * 365;

  // Calculate months (30 days per month for remaining days)
  const months = Math.floor(remaining / 30);
  remaining -= months * 30;

  // Calculate weeks
  const weeks = Math.floor(remaining / 7);
  remaining -= weeks * 7;
  const d = remaining;

  const parts = [];

  if (years) {
    parts.push(`${years} ${t(unitKey('Year', years))}`);
  }
  if (months) {
    parts.push(`${months} ${t(unitKey('Month', months))}`);
  }
  if (weeks) {
    parts.push(`${weeks} ${t(unitKey('Week', weeks))}`);
  }
  if (d) {
    parts.push(`${d} ${t(unitKey('Day', d))}`);
  }

  if (!parts.length) {
    return `${totalDays} ${t(unitKey('Day', totalDays))}`;
  }

  // Limit to maximum of two units (largest-first already by construction)
  const limited = parts.slice(0, 2);
  const andWord = t('DurationAnd');
  if (limited.length === 1) return limited[0];
  return `${limited[0]} ${andWord} ${limited[1]}`;
};

export default formatDuration;
